/**
 * Textos (nome, ajuda e fórmula) dos KPIs de mídia no modo ao vivo. A simulação junta os dela
 * (`packages/metrics/src/kpiTexts.ts`) no `BootPayload`; o app não importa `@rota/metrics` (fronteira
 * 10.2), então os do ao vivo moram aqui.
 */
import type { KpiDef, KpiId } from '@rota/contracts';

type KpiText = Pick<KpiDef, 'label' | 'short' | 'help' | 'formula'>;

/**
 * Textos do modo ao vivo (dados reais da Meta e da Kiwify) onde a conta é outra que a da simulação:
 * o módulo de mídia troca estes no boot (`kpiDefs`; os da simulação ficam em `@rota/metrics`). Regras do dono (28/10/2026): faturamento =
 * valor dos produtos (sem os juros do parcelamento); ROAS = valor das compras que a Meta atribui ÷
 * valor gasto (sem impostos, como o Gerenciador); ROI = esse valor ÷ custo de mídia com impostos e
 * IOF; nenhuma conta com imposto no denominador se chama ROAS; CPA conta só as compras do curso.
 */
export const LIVE_KPI_TEXTS: Readonly<Partial<Record<KpiId, KpiText>>> = {
  spendAm: {
    label: 'Investimento',
    short: 'Investimento',
    help: 'Valor gasto no Gerenciador de Anúncios, sem impostos (a conta em dólar pelo dólar do dia). É a base do CPA e do ROAS.',
    formula: 'Σ valor gasto (Gerenciador)',
  },
  mediaCost: {
    label: 'Custo de mídia',
    short: 'Custo de mídia',
    help: 'O que sai do caixa com anúncios: na conta em real, o gasto com os impostos da Meta (12,15%, cobrados por dentro); na conta em dólar, o gasto pelo dólar do dia com o câmbio do cartão e o IOF. É a base do ROI, do MER, do retorno real e do lucro.',
    formula:
      'conta em real: gasto ÷ (1 − 12,15%) · conta em dólar: gasto × dólar do dia × câmbio do cartão × IOF',
  },
  cpa: {
    label: 'CPA',
    help: 'Investimento por compra do curso que a Meta atribui aos anúncios. As compras do 20X saem pelo valor de cada compra, porque o teto do CPA já soma o 20X por comprador do curso.',
    formula: 'investimento ÷ compras do curso na Meta',
  },
  roas: {
    label: 'ROAS',
    help: 'Valor das compras que a Meta atribui aos anúncios (curso e 20X, preço cheio) por real gasto na Meta, sem impostos nem IOF: o mesmo ROAS de compras do Gerenciador.',
    formula: 'valor das compras na Meta ÷ investimento',
  },
  roi: {
    label: 'ROI',
    help: 'Valor das compras que a Meta atribui aos anúncios por real de custo de mídia, com os impostos da Meta e o IOF: o retorno sobre o que saiu do caixa. 2,47 quer dizer R$ 2,47 em vendas para cada R$ 1 pago.',
    formula: 'valor das compras na Meta ÷ custo de mídia',
  },
  mer: {
    label: 'MER',
    help: 'Valor dos produtos vendidos (todas as origens, sem os juros do parcelamento) por real de custo de mídia com impostos.',
    formula: 'valor dos produtos ÷ custo de mídia',
  },
  roasReal: {
    label: 'Retorno real',
    short: 'Retorno real',
    help: 'O que fica na empresa (valor dos produtos − taxa da Kiwify − reembolsos) por real de custo de mídia com impostos.',
    formula: '(valor dos produtos − taxa da Kiwify − reembolsos) ÷ custo de mídia',
  },
  profit: {
    label: 'Resultado da empresa',
    short: 'Lucro',
    help: 'Valor dos produtos − taxa da Kiwify − reembolsos − custo de mídia com impostos. O repasse da coprodução não sai daqui.',
    formula: 'valor dos produtos − taxa da Kiwify − reembolsos − custo de mídia',
  },
  margin: {
    label: 'Margem',
    help: 'Resultado da empresa como fração do valor dos produtos.',
    formula: 'resultado da empresa ÷ valor dos produtos',
  },
  upsellTakeRate: {
    label: 'Upsell 20X',
    short: 'Upsell',
    help: 'Vendas do 20X por venda do curso no período (Kiwify, todas as origens).',
    formula: 'vendas do 20X ÷ vendas do curso',
  },
};
